// ===================================
// SESSIONS PAGE
// src/pages/Sessions.js
// Updated for AppLayout with new routing paths
// ===================================

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getSessions, getSessionStats } from '../services/sessionsApi';
import './Sessions.css';

const Sessions = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    ready: 0,
    active: 0
  });
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load sessions
      try {
        const sessionsData = await getSessions();
        setSessions(Array.isArray(sessionsData) ? sessionsData : []);
      } catch (err) {
        console.error('Error loading sessions:', err);
        setSessions([]);
      }

      // Load stats
      try {
        const statsData = await getSessionStats();
        setStats({
          total: statsData?.total || 0,
          completed: statsData?.completed || 0,
          ready: statsData?.ready || 0,
          active: statsData?.active || 0
        });
      } catch (err) {
        console.error('Error loading stats:', err);
      }

    } catch (err) {
      console.error('Sessions page error:', err);
      setError('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const filteredSessions = sessions.filter(session => {
    if (filter === 'all') return true;
    if (filter === 'ready') return session.status === 'paid';
    if (filter === 'completed') return session.status === 'completed';
    if (filter === 'active') return session.status === 'active';
    return true;
  });

  const getStatusBadge = (status) => {
    const badges = {
      completed: 'badge-success',
      active: 'badge-warning',
      paid: 'badge-info',
      pending: 'badge-neutral',
      expired: 'badge-error',
      cancelled: 'badge-error'
    };
    return badges[status] || 'badge-neutral';
  };

  const getStatusLabel = (status) => {
    const labels = {
      paid: 'Ready',
      completed: 'Completed',
      active: 'In Progress',
      pending: 'Pending',
      expired: 'Expired',
      cancelled: 'Cancelled'
    };
    return labels[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="sessions-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading sessions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sessions-page">
      <div className="sessions-container">
        {/* Header */}
        <div className="page-header">
          <div className="header-content">
            <h1>Your Sessions</h1>
            <p>View and manage your interview practice sessions</p>
          </div>
          <Link to="/app/book" className="btn btn-primary">
            + Book New Session
          </Link>
        </div>

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {/* Quick Stats */}
        <div className="quick-stats">
          <div className="quick-stat">
            <span className="stat-num">{stats.total}</span>
            <span className="stat-text">Total</span>
          </div>
          <div className="quick-stat">
            <span className="stat-num">{stats.ready}</span>
            <span className="stat-text">Ready</span>
          </div>
          <div className="quick-stat">
            <span className="stat-num">{stats.active}</span>
            <span className="stat-text">Active</span>
          </div>
          <div className="quick-stat">
            <span className="stat-num">{stats.completed}</span>
            <span className="stat-text">Completed</span>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="filter-tabs">
          <button
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({stats.total})
          </button>
          <button
            className={`filter-tab ${filter === 'ready' ? 'active' : ''}`}
            onClick={() => setFilter('ready')}
          >
            Ready ({stats.ready})
          </button>
          <button
            className={`filter-tab ${filter === 'active' ? 'active' : ''}`}
            onClick={() => setFilter('active')}
          >
            Active ({stats.active})
          </button>
          <button
            className={`filter-tab ${filter === 'completed' ? 'active' : ''}`}
            onClick={() => setFilter('completed')}
          >
            Completed ({stats.completed})
          </button>
        </div>

        {/* Sessions Grid */}
        {filteredSessions.length === 0 ? (
          <div className="empty-state-container">
            <div className="empty-state">
              <div className="empty-icon">📭</div>
              <h3>{filter === 'all' ? 'No sessions yet' : `No ${filter} sessions`}</h3>
              <p>
                {filter === 'all'
                  ? 'Book your first interview practice session to get started!'
                  : 'Try selecting a different filter or book a new session.'}
              </p>
              <Link to="/app/book" className="btn btn-primary">
                Book Session
              </Link>
            </div>
          </div>
        ) : (
          <div className="sessions-grid">
            {filteredSessions.map((session) => (
              <div
                key={session.id}
                className="session-card"
                onClick={() => navigate(`/app/sessions/${session.id}`)}
              >
                <div className="card-top">
                  <span className="session-type-icon">
                    {session.interview_type === 'voice' ? '🎙️' : session.interview_type === 'phone' ? '📞' : '💬'}
                  </span>
                  <span className={`badge ${getStatusBadge(session.status)}`}>
                    {getStatusLabel(session.status)}
                  </span>
                </div>

                <h3 className="session-title">
                  {session.interviewer_personality} Interview
                </h3>

                <div className="session-details">
                  <span>{session.duration} min</span>
                  <span>•</span>
                  <span>{session.experience_level}</span>
                </div>

                {session.access_code && session.status === 'paid' && (
                  <div className="access-code-box">
                    <span className="code-label">Access Code</span>
                    <span className="code-value">{session.access_code}</span>
                  </div>
                )}
{/* 
                {session.status === 'completed' && (
                  <div className="score-display">
                    <div className="score-bar">
                      <div
                        className="score-fill"
                        style={{ width: `${session.overall_score || session.score?.overall || 0}%` }}
                      />
                    </div>
                    <span className="score-value">
                      {session.overall_score || session.score?.overall || 0}%
                    </span>
                  </div>
                )} */}

                {session.status === 'completed' && session.category_slug !== 'academy-guidance' && (
  <div className="score-display">
    <div className="score-bar">
      <div
        className="score-fill"
        style={{ width: `${session.overall_score || session.score?.overall || 0}%` }}
      />
    </div>
    <span className="score-value">
      {session.overall_score || session.score?.overall || 0}%
    </span>
  </div>
)}

                <div className="session-footer">
                  <span className="session-date">{formatDate(session.created_at)}</span>
                  {session.status === 'paid' && (
                    <Link
                      to={`/app/start-session?code=${session.access_code}`}
                      className="btn btn-sm btn-primary"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Start
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Sessions;